import { useState, type ReactNode } from "react";
import { acha, fatiarPelaBusca } from "../lib/processos";

// As peças do Leitor Inteligente de Processos (000171).
//
// Nada aqui decide nada: a apresentação do que a IA extraiu, e o botão de copiar
// que o perito usa para levar o texto ao laudo dele — que é a razão de a
// ferramenta existir e o único caminho de "saída" deste corte.

/**
 * Uma seção do relatório, com o botão de copiar quando há o que copiar.
 *
 * "Copiar para o laudo" é o gesto central da ferramenta na origem, e ele
 * continua: o perito escreve o laudo no editor dele, não aqui.
 *
 * O `<section role="group" aria-labelledby>` com `<h3>` comum, e nunca
 * `<fieldset><legend>` — a especificação posiciona o `legend` SOBRE a borda, e
 * com o card arredondado o título saía flutuando fora da caixa (visto na tela em
 * 19/08).
 */
export function Secao({
  titulo,
  paraCopiar,
  children,
}: {
  titulo: string;
  paraCopiar?: string;
  children: ReactNode;
}) {
  const id = `secao-${titulo.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const temTexto = Boolean(paraCopiar && paraCopiar.trim());

  return (
    <section role="group" aria-labelledby={id} className="surface rounded-cf p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id={id} className="font-display text-base font-bold text-titulo">
          {titulo}
        </h3>
        {temTexto && <BotaoCopiar texto={paraCopiar!} rotulo={titulo} />}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/**
 * Copia e DIZ que copiou.
 *
 * O estado volta ao normal em 2 segundos. Sem a confirmação, o perito clica de
 * novo achando que não pegou — e a área de transferência não tem sinal visível
 * por conta própria.
 */
export function BotaoCopiar({ texto, rotulo }: { texto: string; rotulo: string }) {
  const [estado, setEstado] = useState<"parado" | "copiou" | "falhou">("parado");

  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard
          .writeText(texto)
          .then(() => setEstado("copiou"))
          .catch(() => setEstado("falhou"))
          .finally(() => setTimeout(() => setEstado("parado"), 2000));
      }}
      aria-label={`Copiar ${rotulo} para a área de transferência`}
      className="rounded-full border border-borda px-3 py-1 text-xs font-medium text-cinza transition hover:border-laranja hover:text-tinta"
    >
      {estado === "copiou" ? "Copiado" : estado === "falhou" ? "Não deu para copiar" : "Copiar"}
    </button>
  );
}

/**
 * O termo da busca, pintado dentro do texto.
 *
 * Sai como NÓS de texto do React, nunca HTML: a mesma decisão de
 * `lib/textoRico.ts` e de `TextoDaIA` — este texto vem de um modelo alimentado
 * com o processo de um terceiro, e a ausência de `dangerouslySetInnerHTML` no
 * hub é a peça de segurança, não um detalhe de estilo.
 */
export function Marca({ texto, termo }: { texto: string; termo: string }) {
  if (!termo.trim()) return <>{texto}</>;
  return (
    <>
      {fatiarPelaBusca(texto, termo).map((p, i) =>
        p.marcado ? (
          <mark key={i} className="rounded bg-laranja/25 px-0.5 text-tinta">
            {p.texto}
          </mark>
        ) : (
          <span key={i}>{p.texto}</span>
        ),
      )}
    </>
  );
}

/** Um parágrafo do relatório. Texto ausente é DITO, não escondido. */
export function Destaque({ texto, termo = "" }: { texto?: string; termo?: string }) {
  const vazio =
    !texto ||
    !texto.trim() ||
    texto.trim() === "Informação não identificada no documento";

  if (vazio) {
    return (
      <p className="text-sm text-cinza">
        A IA não localizou esta informação no trecho lido.
      </p>
    );
  }
  return (
    <p className="text-sm leading-relaxed text-tinta">
      <Marca texto={texto} termo={termo} />
    </p>
  );
}

/**
 * O campo de busca, e o que ele DIZ sobre onde procura.
 *
 * A busca da origem filtra as quatro abas e não diz nada sobre o alcance dela.
 * Aqui o rótulo é "procurar no relatório", e o vazio oferece a mesma pergunta ao
 * assistente — que lê o texto integral e cita a página. O perito que digita
 * "FGTS" e não acha nada precisa saber que isso NÃO significa que a palavra não
 * está no processo.
 */
export function Procura({
  termo,
  aoTrocar,
  achados,
  aoPerguntar,
}: {
  termo: string;
  aoTrocar: (t: string) => void;
  achados: { total: number };
  aoPerguntar: (termo: string) => void;
}) {
  const procurando = termo.trim().length > 0;

  return (
    <section className="mt-6" aria-label="Procurar no relatório">
      <div className="relative">
        <label htmlFor="procurar-no-relatorio" className="sr-only">
          Procurar no relatório desta análise
        </label>
        <input
          id="procurar-no-relatorio"
          type="search"
          value={termo}
          onChange={(e) => aoTrocar(e.target.value)}
          placeholder="Procurar no relatório…"
          className="w-full rounded-full border border-borda bg-superficie px-4 py-2.5 pr-24 text-sm text-tinta placeholder:text-cinza focus:border-azul focus:outline-none"
        />
        {procurando && (
          <button
            type="button"
            onClick={() => aoTrocar("")}
            className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full px-2 py-0.5 text-xs text-cinza hover:text-tinta"
          >
            limpar
          </button>
        )}
      </div>

      {procurando && (
        <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
          <span className={achados.total > 0 ? "text-cinza" : "font-semibold text-alerta-tinta"}>
            {achados.total > 0
              ? `${achados.total} ${achados.total === 1 ? "item" : "itens"} do relatório com "${termo.trim()}"`
              : `Nada no relatório com "${termo.trim()}"`}
          </span>
          {/* O vazio NÃO é um beco. A busca olha o que a IA extraiu; o termo
              pode estar no processo e não no relatório — e pode estar numa
              página que a IA não leu. Quem sabe do texto integral é o
              assistente. */}
          <button
            type="button"
            onClick={() => aoPerguntar(termo.trim())}
            className="font-semibold text-link underline hover:no-underline"
          >
            perguntar ao assistente sobre "{termo.trim()}"
          </button>
          <span className="text-cinza">
            — a busca olha o relatório; o assistente lê o texto do processo.
          </span>
        </div>
      )}
    </section>
  );
}

/**
 * De qual arquivo o item saiu. Só aparece com mais de um arquivo: com um só, a
 * resposta é óbvia e a etiqueta é ruído.
 *
 * A cor sai da POSIÇÃO do arquivo na lista, e não de um sorteio — dois itens do
 * mesmo PDF têm sempre a mesma cor, que é o que faz a etiqueta informar algo.
 */
const CORES_DE_FONTE = [
  "bg-chip text-chip-tinta",
  "bg-sucesso/15 text-sucesso",
  "bg-alerta-fundo text-alerta-tinta",
  "bg-erro-fundo text-erro-tinta",
  "bg-azul/12 text-link",
];

export function Fonte({ nome, nomes }: { nome?: string; nomes: string[] }) {
  if (!nome || nomes.length <= 1) return null;
  const i = nomes.indexOf(nome);
  const cor = CORES_DE_FONTE[(i < 0 ? nomes.length : i) % CORES_DE_FONTE.length];
  const curto = nome.length > 24 ? `${nome.slice(0, 21)}…` : nome;

  return (
    <span
      title={nome}
      className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold whitespace-nowrap ${cor}`}
    >
      {curto}
    </span>
  );
}

export type Evento = {
  data?: string;
  evento?: string;
  descricao?: string;
  pagina?: string;
  fonte?: string;
};

/**
 * A linha do tempo processual.
 *
 * A ordem é a que a IA devolveu — o prompt manda organizar cronologicamente, e
 * reordenar aqui em JavaScript criaria uma segunda opinião sobre a cronologia a
 * partir de datas em texto livre ("15/03/2024", "março de 2024", "sem data").
 * Data que não parseia iria para uma ponta e o perito não saberia por quê.
 */
export function LinhaDoTempo({
  eventos,
  termo = "",
  escondidos = 0,
}: {
  eventos: Evento[];
  termo?: string;
  escondidos?: number;
}) {
  if (eventos.length === 0) {
    return (
      <p className="surface rounded-cf p-5 text-sm text-tinta">
        {escondidos > 0
          ? // A distinção importa: "o filtro escondeu tudo" pede limpar a busca,
            // "a IA não achou nada" pede outra coisa. Confundir as duas manda o
            // perito procurar um defeito na ferramenta.
            `Nenhum dos ${escondidos} atos processuais casa com a busca. Limpe o campo para ver todos.`
          : "A IA não identificou atos processuais com data no trecho lido."}
      </p>
    );
  }

  return (
    <section className="surface rounded-cf p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-display text-base font-bold text-titulo">
          {eventos.length} ato{eventos.length === 1 ? "" : "s"} processua{eventos.length === 1 ? "l" : "is"}
        </h3>
        <BotaoCopiar
          rotulo="a linha do tempo"
          texto={eventos
            .map((e) => `${e.data ?? "sem data"} — ${e.evento ?? ""}: ${e.descricao ?? ""}${e.pagina ? ` (${e.pagina})` : ""}`)
            .join("\n")}
        />
      </div>

      <ol className="mt-4 grid gap-0">
        {eventos.map((e, i) => (
          <li key={i} className="relative grid gap-1 pb-5 pl-6 last:pb-0">
            {/* O trilho e o ponto. `aria-hidden`: a lista ordenada já diz ao
                leitor de tela que é uma sequência. */}
            <span
              aria-hidden="true"
              className="absolute top-1.5 left-0 size-2.5 rounded-full bg-laranja"
            />
            {i < eventos.length - 1 && (
              <span
                aria-hidden="true"
                className="absolute top-4 bottom-0 left-[0.3125rem] w-px bg-borda"
              />
            )}

            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-xs font-semibold text-link tabular-nums">
                {e.data ?? "sem data"}
              </span>
              {e.pagina && <span className="text-xs text-cinza">{e.pagina}</span>}
            </div>
            <p className="text-sm font-semibold text-titulo">
              <Marca texto={e.evento ?? ""} termo={termo} />
            </p>
            {e.descricao && (
              <p className="text-sm text-tinta">
                <Marca texto={e.descricao} termo={termo} />
              </p>
            )}
          </li>
        ))}
      </ol>

      {escondidos > 0 && (
        <p className="mt-3 text-xs text-cinza">
          {escondidos} {escondidos === 1 ? "ato" : "atos"} sem o termo da busca{" "}
          {escondidos === 1 ? "está" : "estão"} escondido{escondidos === 1 ? "" : "s"}.
        </p>
      )}
    </section>
  );
}

export type Alerta = { tipo?: string; descricao?: string; pagina?: string; fonte?: string };

/**
 * Os alertas, agrupáveis por tipo.
 *
 * O filtro por tipo só aparece com mais de um tipo — a origem mostra a barra de
 * filtro com um botão único, que não filtra nada e ocupa uma linha.
 */
export function ListaDeAlertas({
  alertas,
  termo = "",
  escondidos = 0,
}: {
  alertas: Alerta[];
  termo?: string;
  escondidos?: number;
}) {
  const [tipo, setTipo] = useState<string | null>(null);
  const tipos = Array.from(new Set(alertas.map((a) => a.tipo).filter(Boolean) as string[]));

  if (alertas.length === 0 && escondidos > 0) {
    return (
      <p className="surface rounded-cf p-5 text-sm text-tinta">
        Nenhum dos {escondidos} alertas casa com a busca. Limpe o campo para ver todos.
      </p>
    );
  }

  if (alertas.length === 0) {
    return (
      <section className="surface rounded-cf p-5">
        <h3 className="font-display text-base font-bold text-titulo">Nenhum alerta</h3>
        {/* Ausência de alerta NÃO é atestado de que o processo está completo, e
            dizer isso é o oposto de esconder: a linha condicional que carrega um
            aviso precisa de texto próprio no ramo em que ela não aparece — a
            cicatriz do "Limite com a prorrogação" do HUB DP. */}
        <p className="mt-2 text-sm text-tinta">
          A IA não apontou inconsistência nem documento ausente no trecho lido. Isso não
          quer dizer que o processo esteja completo — só que nada saltou na leitura.
        </p>
      </section>
    );
  }

  const visiveis = tipo ? alertas.filter((a) => a.tipo === tipo) : alertas;

  return (
    <div className="grid gap-3">
      {tipos.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setTipo(null)}
            aria-pressed={tipo === null}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              tipo === null
                ? "bg-azul text-white"
                : "border border-borda text-cinza hover:border-laranja hover:text-tinta"
            }`}
          >
            Todos
          </button>
          {tipos.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTipo(t)}
              aria-pressed={tipo === t}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                tipo === t
                  ? "bg-azul text-white"
                  : "border border-borda text-cinza hover:border-laranja hover:text-tinta"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      <ul className="grid gap-2">
        {visiveis.map((a, i) => (
          <li
            key={i}
            className="surface rounded-cf border-l-4 border-l-alerta-tinta p-4"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-alerta-tinta">
                <Marca texto={a.tipo ?? "Atenção"} termo={termo} />
              </span>
              {a.pagina && <span className="text-xs text-cinza">{a.pagina}</span>}
            </div>
            {a.descricao && (
              <p className="mt-1 text-sm text-tinta">
                <Marca texto={a.descricao} termo={termo} />
              </p>
            )}
          </li>
        ))}
      </ul>

      {escondidos > 0 && (
        <p className="text-xs text-cinza">
          {escondidos} alerta{escondidos === 1 ? "" : "s"} sem o termo da busca{" "}
          {escondidos === 1 ? "está" : "estão"} escondido{escondidos === 1 ? "" : "s"}.
        </p>
      )}
    </div>
  );
}

/** O que a busca alcança num alerta. Um lugar só — a tela filtra e o chip conta. */
export function alertaCasa(a: Alerta, termo: string): boolean {
  if (!termo.trim()) return true;
  return acha(a.tipo, termo) || acha(a.descricao, termo) || acha(a.pagina, termo);
}

/** O que a busca alcança num ato processual. */
export function eventoCasa(e: Evento, termo: string): boolean {
  if (!termo.trim()) return true;
  return (
    acha(e.evento, termo) ||
    acha(e.descricao, termo) ||
    acha(e.data, termo) ||
    acha(e.pagina, termo)
  );
}

// ===========================================================================
// O BALÃO FLUTUANTE do assistente
// ===========================================================================
// A peça SAIU deste arquivo em 14/09/2026, quando a Central IBS-CBS passou a
// precisar da mesma coisa — ela não tinha nada do Leitor dentro, e uma segunda
// cópia divergiria da primeira em silêncio. O porquê de cada decisão dela (a
// altura que desconta `--cf-topo`, o Escape que devolve o foco pelo efeito, o
// disclosure em vez de `<dialog>`) mora agora em `BalaoDoAssistente.tsx`.
//
// A reexportação fica: `LeitorAnalise.tsx` importa daqui, e trocar o caminho de
// importação não melhora nada — o que importava era não haver duas peças.
export { BalaoDoAssistente } from "./BalaoDoAssistente";
